/**
 * CI diária (`TP-056`, T5/T6): os retornos da operadora ainda a confirmar, do núcleo da sessão.
 *
 * A CI é o retorno diário da operadora com o status da autorização (glossário). No Bradesco ele chega como a carta
 * da SIAI; o resultado é marcado na triagem (por uma pessoa olhando a imagem, ou pelo agente quando ele ler a carta)
 * e é aqui que se confirma o que ele propõe ao caso. Nada transita sozinho. O retorno sem transição possível aparece
 * com "só registrar", e não some.
 */

import Button from '@mui/material/Button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { formatarDataHora } from '../../shared/dominio/datas';
import { ORIGENS_DO_CASO, caminhoDoCaso } from '../../shared/navegacao/mapaDeNavegacao';
import { confirmarRetorno, descartarRetorno, opcoesDosRetornosAConfirmar } from '../../shared/retornos/api';
import type { MotivoDoDescarte, RetornoAConfirmar } from '../../shared/retornos/tipos';
import {
  MOTIVOS_PARA_ESCOLHA,
  ROTULOS_DA_PROPOSTA,
  ROTULOS_DO_MOTIVO,
  ROTULOS_DO_RESULTADO,
  mensagemDeErroDoRetorno,
  rotuloDaOrigem,
  textoDaConfirmacao,
} from '../../shared/retornos/vocabulario';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';
import { Tabela, Td } from '../../shared/ui/primitivas';
import { TelaAnery } from '../../shared/ui/TelaAnery';

const COLUNAS = [
  { rot: 'Chegou' },
  { rot: 'Caso' },
  { rot: 'Dono' },
  { rot: 'Resultado' },
  { rot: 'Propõe' },
  { rot: 'Decisão' },
];

function LinhaDoRetorno({
  retorno,
  ocupado,
  aoConfirmar,
  aoDescartar,
}: {
  retorno: RetornoAConfirmar;
  ocupado: boolean;
  aoConfirmar: () => void;
  aoDescartar: (motivo: MotivoDoDescarte) => void;
}): React.JSX.Element {
  const [motivo, definirMotivo] = useState<MotivoDoDescarte>('WrongReading');
  return (
    <tr>
      <Td>{formatarDataHora(retorno.recebidoEm)}</Td>
      <Td>
        <Link to={caminhoDoCaso(retorno.casoId)} state={{ origem: ORIGENS_DO_CASO.ciDiaria }}>
          {retorno.paciente ?? 'Caso sem nome de exibição'}
        </Link>
      </Td>
      <Td fraco>{retorno.dono ?? 'sem dono'}</Td>
      <Td>
        <span className="block">{ROTULOS_DO_RESULTADO[retorno.resultado]}</span>
        <span className="block text-xs text-[var(--tinta-2)]">{rotuloDaOrigem(retorno.origem, retorno.confianca)}</span>
      </Td>
      <Td>{ROTULOS_DA_PROPOSTA[retorno.proposta]}</Td>
      <Td>
        <span className="flex flex-wrap items-center gap-2">
          <Button size="small" variant="contained" disabled={ocupado} onClick={aoConfirmar}>
            {textoDaConfirmacao(retorno.proposta, retorno.recebidoEm, retorno.efetivoEm)}
          </Button>
          <label className="text-xs">
            <span className="sr-only">Motivo do descarte</span>
            <select
              aria-label={`Motivo do descarte do retorno de ${formatarDataHora(retorno.recebidoEm)}`}
              value={motivo}
              onChange={(evento) => {
                definirMotivo(evento.target.value as MotivoDoDescarte);
              }}
            >
              {MOTIVOS_PARA_ESCOLHA.map((m) => (
                <option key={m} value={m}>
                  {ROTULOS_DO_MOTIVO[m]}
                </option>
              ))}
            </select>
          </label>
          <Button
            size="small"
            variant="outlined"
            disabled={ocupado}
            onClick={() => {
              aoDescartar(motivo);
            }}
          >
            Descartar
          </Button>
        </span>
      </Td>
    </tr>
  );
}

export function PaginaDaCiDiaria(): React.JSX.Element {
  const [meus, definirMeus] = useState(false);
  const consulta = useQuery(opcoesDosRetornosAConfirmar(meus));
  const clienteDeConsultas = useQueryClient();

  const aoDecidir = (): void => {
    void clienteDeConsultas.invalidateQueries({ queryKey: ['retornos'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['casos'] });
  };

  // Também no erro: um 409 quer dizer que outra pessoa já decidiu esse retorno, e a linha não pode ficar na lista.
  const confirmar = useMutation({
    mutationFn: (r: RetornoAConfirmar) => confirmarRetorno(r.casoId, r.emailId),
    onSuccess: aoDecidir,
    onError: aoDecidir,
  });
  const descartar = useMutation({
    mutationFn: ({ r, motivo }: { r: RetornoAConfirmar; motivo: MotivoDoDescarte }) =>
      descartarRetorno(r.casoId, r.emailId, motivo),
    onSuccess: aoDecidir,
    onError: aoDecidir,
  });

  const ocupado = confirmar.isPending || descartar.isPending;
  const erro = confirmar.error ?? descartar.error;

  return (
    <TelaAnery
      breadcrumb="operação / CI diária"
      titulo="CI diária — retornos da operadora a confirmar"
      subtitulo="O que a operadora decidiu em cada carta da SIAI, e o que isso faz com o caso. Nada muda sem um clique, e a hora gravada é a da chegada do e-mail."
    >
      <label className="mb-3 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={meus}
          onChange={(evento) => {
            definirMeus(evento.target.checked);
          }}
        />
        Só os casos de que sou dono
      </label>

      {consulta.isPending ? <EstadoDeCarregamento linhas={4} /> : null}

      {consulta.isError ? (
        <EstadoDeErro
          erro={consulta.error}
          contexto="Não foi possível carregar os retornos"
          aoReprocessar={() => {
            void consulta.refetch();
          }}
        />
      ) : null}

      {consulta.isSuccess && consulta.data.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum retorno a confirmar"
          descricao={
            meus
              ? 'Os casos de que você é dono não têm retorno da operadora esperando confirmação.'
              : 'Nenhum retorno da operadora do seu núcleo está esperando confirmação.'
          }
        />
      ) : null}

      {consulta.isSuccess && consulta.data.length > 0 ? (
        <Tabela colunas={COLUNAS}>
          {consulta.data.map((retorno) => (
            <LinhaDoRetorno
              key={retorno.emailId}
              retorno={retorno}
              ocupado={ocupado}
              aoConfirmar={() => {
                descartar.reset();
                confirmar.mutate(retorno);
              }}
              aoDescartar={(motivo) => {
                confirmar.reset();
                descartar.mutate({ r: retorno, motivo });
              }}
            />
          ))}
        </Tabela>
      ) : null}

      {erro ? (
        <p role="alert" aria-live="polite" className="mt-3 text-[0.8125rem] text-[var(--roxo)]">
          {mensagemDeErroDoRetorno(erro)}
        </p>
      ) : null}
    </TelaAnery>
  );
}
